// ── AssignModal ───────────────────────────────────────────────────────────────
// CISO-only modal to create a new assignment on a control.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useCreateAssignment } from './api';

interface Props {
  controlId: string;
  open: boolean;
  onClose: () => void;
}

export function AssignModal({ controlId, open, onClose }: Props) {
  const createMutation = useCreateAssignment(controlId);

  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [assigneeDisplayName, setAssigneeDisplayName] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [note, setNote] = useState('');

  function handleClose() {
    setAssigneeEmail('');
    setAssigneeDisplayName('');
    setDueAt('');
    setNote('');
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate(
      {
        control_id: controlId,
        assignee_email: assigneeEmail.trim(),
        assignee_display_name: assigneeDisplayName.trim() || undefined,
        // verifier_user_id not known here — for now we allow by-email verifier lookup
        // in a future iteration, add a user-picker. D.1 uses email for both.
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Assignment created');
          handleClose();
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { errors?: { message?: string }[] } } })
            ?.response?.data?.errors?.[0]?.message ?? 'Failed to create assignment';
          toast.error(msg);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Control</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Assignee email *</Label>
            <Input
              type="email"
              placeholder="assignee@company.com"
              value={assigneeEmail}
              onChange={(e) => setAssigneeEmail(e.target.value)}
              required
              className="text-sm"
            />
            <p className="text-[10px] text-text-secondary">
              If this email doesn't match a platform user, a pending assignee record will be created.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Assignee display name (optional)</Label>
            <Input
              placeholder="Full name"
              value={assigneeDisplayName}
              onChange={(e) => setAssigneeDisplayName(e.target.value)}
              className="text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Due date (optional)</Label>
            <input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Note (optional)</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Any initial context for the assignee"
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none resize-none"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose}>Cancel</Button>
            <Button type="submit" disabled={createMutation.isPending || !assigneeEmail.trim()}>
              {createMutation.isPending ? 'Creating…' : 'Create Assignment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
